import React, { useState, useEffect, useRef } from 'react';
import FormSection from '../FormSection';
import FormField from '../FormField';
import { ThematicAxis } from '../../types/profile.types';
import { thematicAxes, subejesPorEje } from '../../data/profileData';
import './ThematicAxesSection.css';
interface ThematicAxesSectionProps {
  selectedAxes: ThematicAxis[];
  onChange: (axes: ThematicAxis[]) => void;
  error?: string;
  readOnly?: boolean;
  /** Cuando true, se usa en el modal Crear contacto; sin título/descripción y campo no obligatorio */
  createContactMode?: boolean;
}
const ThematicAxesSection: React.FC<ThematicAxesSectionProps> = ({ selectedAxes, onChange, error, readOnly = false, createContactMode = false }) => {
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  // Detectar si es mobile o tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    // Limpiar tooltip existente primero
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }
    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = thematicAxes[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado.label]?.subejes || [];
      if (subejes.length === 0) return;
      // Detectar si está en modo oscuro
      const isDarkMode = document.documentElement.classList.contains('dark-theme');
      // Crear el elemento del tooltip
      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;
      // Crear el contenido
      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
      titleDiv.textContent = 'Incluye:';
      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';
      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });
      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);
      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;
      }
      // Cleanup
      return () => {
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          tooltipRef.current = null;
        }
      };
    }
  }, [visibleTooltip, tooltipPosition]);
  // Cerrar tooltip al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (!isMobileOrTablet || visibleTooltip === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.checkbox-item')) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTablet, visibleTooltip]);
  const handleChange = (value: ThematicAxis, checked: boolean) => {
    const newAxes = checked 
      ? [...selectedAxes, value]
      : selectedAxes.filter(v => v !== value);
    onChange(newAxes);
  };
  return (
    <FormSection 
      title={createContactMode ? undefined : 'Ejes Temáticos Asociados'} 
      description={createContactMode ? undefined : (
        <>
          Selecciona uno o varios ejes temáticos (Conoce más de cada eje en{' '}
          <a 
            href="https://smartcityexposantiagodelestero.com/tematicas" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#CE2B15', 
              textDecoration: 'none',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Temáticas
          </a>
          )
        </>
      )}
      required={!createContactMode}
    >
      <FormField label="Ejes de interés de la organización" required={!createContactMode} error={error}>
        <div className={`checkbox-group thematic-axes-two-columns ${error ? 'error' : ''}`}>
          {/* Selector "Seleccionar todos" */}
          <label 
            className="checkbox-item select-all-checkbox"
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              width: '100%'
            }}
          >
            <input
              type="checkbox"
              checked={
                selectedAxes.length > 0 &&
                selectedAxes.length === thematicAxes.length
              }
              onChange={(e) => {
                if (readOnly) return;
                if (e.target.checked) {
                  // Seleccionar todos
                  const allAxes = thematicAxes.map(axis => axis.value);
                  onChange(allAxes);
                } else {
                  // Deseleccionar todos
                  onChange([]);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label">Seleccionar todos</span>
          </label>
          {thematicAxes.map((option, index) => {
            const subejes = subejesPorEje[option.label]?.subejes || [];
            const hasSubejes = subejes.length > 0;
            return (
              <label 
                key={option.value} 
                className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                onMouseEnter={(e) => {
                  // En desktop: mostrar tooltip al hacer hover
                  if (hasSubejes && !isMobileOrTablet) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 300;
                    const spacing = 10;
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;
                    // Verificar si se sale por la derecha
                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    // Verificar si se sale por la izquierda
                    if (left < 10) {
                      left = 10;
                    }
                    // Verificar si se sale por abajo
                    const tooltipHeight = 200;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    // Verificar si se sale por arriba
                    if (top < 10) {
                      top = 10;
                    }
                    setVisibleTooltip(index);
                    setTooltipPosition({ left, top });
                  }
                }}
                onMouseLeave={() => {
                  // En desktop: ocultar tooltip al salir del hover
                  if (!isMobileOrTablet) {
                    setVisibleTooltip(null);
                    setTooltipPosition(null);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedAxes.includes(option.value)}
                  onChange={(e) => {
                    if (readOnly) return;
                    handleChange(option.value, e.target.checked);
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label">{option.label}</span>
                {hasSubejes && isMobileOrTablet && (
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      display: 'flex'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Si el tooltip ya está visible para este eje, cerrarlo
                      if (visibleTooltip === index) {
                        setVisibleTooltip(null);
                        setTooltipPosition(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 300;
                      const spacing = 10;
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      // Ajustar posición si se sale de la pantalla
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      if (left < 10) {
                        left = 10;
                      }
                      const tooltipHeight = 200;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      if (top < 10) {
                        top = 10;
                      }
                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }}
                  >
                    ?
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FormField>
    </FormSection>
  );
};
export default ThematicAxesSection;
